import React, { useEffect, useRef, useState } from 'react';
import { Questionnaire } from 'fhir/r4';
import {
  Box,
  Button,
  focusOutlineOuter,
  HeadingText,
  Spinner,
  styled,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { useDocumentTitle, useGoBack } from '@leagueplatform/web-common';
import { DynamicFHIRForm } from '@leagueplatform/fhir-questionnaire';
import { MessagingQuestionnaireResponse } from '@leagueplatform/messaging-api';
import { LEAGUE_MODULE_NAMES, getModulePath } from '@leagueplatform/core';
import { useNavigateLegacy as useNavigate } from '@leagueplatform/routing';
import { EVENT_NAME } from '@leagueplatform/analytics';
import { ErrorState } from '@leagueplatform/web-common-components';
import { handleStaticAsset } from '@leagueplatform/asset-config';
import { Slide } from '#components/slide.component.tsx';
import { useCreateMessageModalWithFhir } from '#hooks/use-create-message-modal-with-fhir.hook.ts';
import { useGetFirstQuestionnaireIdConfig } from '#hooks/use-get-first-questionnaire-id-config.hook.ts';
import { useSetDraftThread } from '#hooks/use-set-draft-thread.hook.ts';
import {
  MESSAGING_SCREEN_NAMES,
  trackMessagingAnalyticsEvent,
} from '#utils/track-messaging-analytics.util.ts';
import { MESSAGING_ASSET_KEYS } from '#types/messaging-assets.types.ts';
import { useGetMessagingConfig } from '#hooks/use-get-messaging-config.hook.ts';

interface LastRequestType {
  fn: (data: any) => void;
  data: any;
}

const Wrapper = styled(Box, {
  backgroundColor: '$surfaceBackgroundTertiary',
  display: 'flex',
  flexDirection: 'column',
  position: 'absolute',
  inset: 0,
  overflowX: 'hidden',
});
const Body = styled(Box, {
  flex: 1,
  paddingTop: '$threeQuarters',
});
const Footer = styled(Box, {
  display: 'flex',
  flexGrow: 0,
  justifyContent: 'center',
  paddingTop: '$oneAndHalf',
  paddingBottom: '$oneAndHalf',
});
const Slider = styled(Box, {
  display: 'flex',
  position: 'relative',
  flexGrow: 1,
  height: '100%',
  width: '100%',
  maxWidth: 632,
  margin: '0 auto',
});
const SlideWrap = styled(Box, {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  backgroundColor: '$surfaceBackgroundPrimary',
  padding: '$two',
  borderRadius: '$large',
  overflowY: 'auto',
  '&&:focus': {
    outline: 'none !important',
  },
  '&&:focus-visible': {
    ...focusOutlineOuter,
  },
});
const ErrorWrapper = styled(SlideWrap, {
  justifyContent: 'center',
  position: 'absolute',
  inset: 0,
  zIndex: 9,
});
const LoadingWrapper = styled(Box, {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
});

export const NewMessagePage = () => {
  const { formatMessage } = useIntl();
  const goBack = useGoBack();

  const [pages, setPages] = useState<Array<any>>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentQuestionnaireId, setCurrentQuestionnaireId] = useState('');
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState<
    Array<MessagingQuestionnaireResponse>
  >([]);
  const [lastRequest, setLastRequest] = useState<LastRequestType | null>(null);
  const formRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const questionnaireContainerRef = useRef<HTMLDivElement>(null);

  const setDraftThread = useSetDraftThread();

  const navigate = useNavigate();

  useDocumentTitle(
    formatMessage({ id: 'MESSAGING_MESSAGING_QUESTIONNAIRE_PAGE_TITLE' }),
  );

  const {
    error: messagingConfigError,
    isError: messagingConfigIsError,
    isLoading: messagingConfigIsLoading,
  } = useGetMessagingConfig();

  useEffect(() => {
    trackMessagingAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
      screen_name: MESSAGING_SCREEN_NAMES.NEW_MESSAGE,
    });
  }, []);

  // update currentPage after pages state is updated in nextStep/prevStep
  // move to next slide
  useEffect(() => {
    if (pages.length > 0) {
      setCurrentPage(pages.length - 1);
    }
  }, [pages]);

  const { firstQuestionnaireId = '' } = useGetFirstQuestionnaireIdConfig();

  const { generateQuestionnaireMutation, postNextNodeMutation } =
    useCreateMessageModalWithFhir();

  // add next page
  const nextStep = (data: any) => {
    setPages((current) => [...current, data]);
  };

  const setQuestionnaireData = (data: any, variables: any) => {
    setCurrentQuestionnaireId(variables.questionnaireId);
    nextStep(data);
  };

  const getQuestionnaireData = ({
    questionnaireId,
    answer,
  }: {
    questionnaireId: string;
    answer: MessagingQuestionnaireResponse | null;
  }) => {
    // Save last request to retry if it fails
    setLastRequest({
      fn: getQuestionnaireData,
      data: {
        questionnaireId,
        answer,
      },
    });

    generateQuestionnaireMutation.mutate(
      {
        questionnaireId,
        answers: answer ? [answer] : [],
      },
      {
        onSuccess: (data, variables) => {
          // Clean last request because it succeeded
          setLastRequest(null);
          setQuestionnaireData(data, variables);
          questionnaireContainerRef.current?.focus();
        },
      },
    );
  };

  // set the firstQuestionnaireId as the current questionnaire ID
  useEffect(() => {
    if (firstQuestionnaireId) {
      getQuestionnaireData({
        questionnaireId: firstQuestionnaireId,
        answer: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstQuestionnaireId]);

  const nextQuestionnaire = (answer: MessagingQuestionnaireResponse) => {
    // Save last request to retry if it fails
    setLastRequest({
      fn: nextQuestionnaire,
      data: answer,
    });

    setQuestionnaireAnswers((current) => [...current, answer]);

    postNextNodeMutation.mutate(
      {
        nodeId: currentQuestionnaireId,
        answers: [answer],
      },
      {
        onSuccess({ data }) {
          // Clean last request because it succeeded
          setLastRequest(null);

          if (data?.attributes?.completed) {
            const newDraftThread = {
              subject: formatMessage({ id: 'NEW_MESSAGE' }),
              participants: [],
              answers: [...questionnaireAnswers, answer], // Include the new answer
            };

            setDraftThread(newDraftThread);
            navigate(`${getModulePath(LEAGUE_MODULE_NAMES.messaging)}/threads`);
            return;
          }
          getQuestionnaireData({
            questionnaireId: data?.attributes?.nodeId,
            answer,
          });
        },
      },
    );
  };

  const setFormRefForIndex =
    (index: number) => (el: HTMLButtonElement | null) => {
      formRefs.current[index] = el;
    };

  // remove current page from stack and update questionnaire id
  const prevStep = () => {
    // This condition avoids double click on the back button
    if (!isTransitioning) {
      const previousPage = pages[currentPage - 1];
      setIsTransitioning(true);
      setCurrentQuestionnaireId(previousPage?.data?.id);
      setTimeout(() => {
        setPages(pages.slice(0, -1));
        setIsTransitioning(false);
      }, 500);
    }
  };

  const triggerFormSubmit = () => {
    if (formRefs.current[currentPage]) {
      const event = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
      });

      const pageTitle = pages[currentPage]?.data?.attributes?.response?.title;
      trackMessagingAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
        screen_name: MESSAGING_SCREEN_NAMES.NEW_MESSAGE,
        detail: `${pageTitle} - Continue`,
      });
      formRefs.current[currentPage].dispatchEvent(event);
    }
  };

  const isLoading =
    generateQuestionnaireMutation.isLoading ||
    postNextNodeMutation.isLoading ||
    messagingConfigIsLoading;

  const isError =
    generateQuestionnaireMutation.isError ||
    postNextNodeMutation.isError ||
    messagingConfigIsError;

  const errors = [
    generateQuestionnaireMutation.error,
    postNextNodeMutation.error,
    messagingConfigError,
  ];

  const errorImage = handleStaticAsset(
    MESSAGING_ASSET_KEYS.MESSAGING_ERROR,
  ) as string;

  const retryRequest = () => {
    lastRequest?.fn(lastRequest?.data);
  };

  return (
    <Wrapper className="new-message-page-wrapper">
      <Body>
        <Button
          onClick={goBack}
          priority="secondary"
          icon="tinyClose"
          hideLabel
          quiet
          size="small"
          css={{
            position: 'absolute',
            top: '$threeQuarters',
            insetInlineEnd: 40,
            zIndex: 1,
          }}
        >
          {formatMessage({ id: 'CLOSE' })}
        </Button>
        <Slider>
          {isError && (
            <ErrorWrapper>
              <ErrorState hideButton imageSrc={errorImage} error={errors} />
            </ErrorWrapper>
          )}
          {isLoading && !pages.length && (
            <LoadingWrapper>
              <Spinner loading />
            </LoadingWrapper>
          )}
          {pages.map((page, index) => (
            <Slide
              key={page.data?.id}
              activeIndex={currentPage}
              gap="120%"
              slideIndex={index}
            >
              <SlideWrap
                ref={questionnaireContainerRef}
                tabIndex={-1}
                className="new-message-page-questionnaire-wrapper"
              >
                <HeadingText
                  level="1"
                  // size is overwritten by typography styles below
                  size="xl"
                  css={{ typography: '$overline' }}
                  emphasis="subtle"
                >
                  {formatMessage({ id: 'MESSAGE_CENTER' })}
                </HeadingText>
                <DynamicFHIRForm
                  FHIRQuestionnaire={
                    pages[index]?.data?.attributes?.response as Questionnaire
                  }
                  setQuestionnaireAnswer={nextQuestionnaire}
                  handleSubmitInParent
                  ref={setFormRefForIndex(index)}
                />
              </SlideWrap>
            </Slide>
          ))}
        </Slider>
      </Body>
      <Footer className="new-message-page-footer">
        {pages.length > 1 && (
          <Button
            onClick={prevStep}
            quiet
            priority="secondary"
            css={{
              marginInlineEnd: '$one',
            }}
          >
            {formatMessage({ id: 'GO_BACK' })}
          </Button>
        )}
        {!isError && (
          <Button onClick={triggerFormSubmit} loading={isLoading}>
            {formatMessage({ id: 'CONTINUE' })}
          </Button>
        )}
        {isError && (
          <Button onClick={retryRequest}>
            {formatMessage({ id: 'RETRY' })}
          </Button>
        )}
      </Footer>
    </Wrapper>
  );
};
